// @ts-nocheck
import { convertDateOnlyStringToDate } from "@insite/client-framework/Common/Utilities/DateUtilities";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import translate from "@insite/client-framework/Translate";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TagWrapper, { TagWrapperProps } from "@store/Library/Components/Toolkit/TagWrapper";
import { tableFilterProps } from "@store/Themes/common/props/table";
import { useShipmentHistoryState } from "@store/Framework/Store/Greif/Pages/ShipmentHistory/ShipmentHistoryHooks";
import { getShipmentStatusesDataView } from "@store/Framework/Store/Greif/Data/ShipmentStatuses/ShipmentStatusesSelectors";
import updateParameter from "@store/Framework/Store/Greif/Pages/ShipmentHistory/Handlers/UpdateParameter";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { useSession } from "@toolkit/Framework/Store/Context/ContextHooks";
import React, { useEffect, useState } from "react";

export interface ShipmentHistoryFilterTagsStyles {
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
    tagWrapper?: TagWrapperProps;
}

export const shipmentHistoryFilterTagsStyles: ShipmentHistoryFilterTagsStyles = {
    tagWrapper: tableFilterProps.tagWrapper,
};

const styles = shipmentHistoryFilterTagsStyles;

const ShipmentHistoryFilterTags = () => {
    const dispatch = useAppDispatch();
    const { language } = useSession();

    const {
        search,
        greifOrderNumber,
        purchaseOrderNumber,
        customerSequence,
        itemNumber,
        deliveryStatuses,
        fromDate,
        toDate,
        isNextDaysFilter,
        location: locationFilter,
    } = useShipmentHistoryState().parameter;

    const shipmentStatusesDataViewValues = useAppSelector(state => getShipmentStatusesDataView(state).value);
    const locations = useAppSelector(state => state.greif.components.locationFilters.locations);

    const [locationDescription, setLocationDescription] = useState<string>();

    useEffect(() => {
        const loc = locations.filter(x => x.location === locationFilter);
        setLocationDescription(loc[0]?.locationDescription);
    }, [locations, locationFilter]);

    const getStatusDisplay = (status: string) => {
        return shipmentStatusesDataViewValues?.find(x => x.name === status)?.description || translate("N/A");
    };

    if (
        !search &&
        !greifOrderNumber &&
        !purchaseOrderNumber &&
        (!customerSequence || customerSequence === "-1") &&
        !itemNumber &&
        !deliveryStatuses?.length &&
        !fromDate &&
        !toDate &&
        !isNextDaysFilter &&
        !locationFilter
    ) {
        return null;
    }

    return (
        <TagWrapper {...styles.tagWrapper}>
            {search && (
                <Tag
                    {...styles.appliedFilterTag}
                    data-test-selector="shipmentHistory_quickSearchTag"
                    onDelete={() => dispatch(updateParameter({ search: undefined }))}
                >
                    {`${translate("{0}:", translate("Quick Search"))} ${search}`}
                </Tag>
            )}
            {greifOrderNumber && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => dispatch(updateParameter({ greifOrderNumber: "" }))}
                    data-test-selector="shipmentHistory_greifOrderTag"
                >
                    {`${translate("{0}:", translate("Greif Order #"))} ${greifOrderNumber}`}
                </Tag>
            )}
            {purchaseOrderNumber && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => dispatch(updateParameter({ purchaseOrderNumber: "" }))}
                    data-test-selector="shipmentHistory_purchaseOrderTag"
                >
                    {`${translate("{0}:", translate("PO #"))} ${purchaseOrderNumber}`}
                </Tag>
            )}
            {customerSequence && customerSequence !== "-1" && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => dispatch(updateParameter({ customerSequence: "-1" }))}
                    data-test-selector="shipmentHistory_shipToTag"
                >
                    {`${translate("{0}:", translate("Ship To"))} ${customerSequence}`}
                </Tag>
            )}
            {itemNumber && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => dispatch(updateParameter({ itemNumber: "" }))}
                    data-test-selector="shipmentHistory_itemNumberTag"
                >
                    {`${translate("{0}:", translate("Item #"))} ${itemNumber}`}
                </Tag>
            )}
            {deliveryStatuses &&
                deliveryStatuses.map(status => (
                    <Tag
                        key={status}
                        {...styles.appliedFilterTag}
                        onDelete={() => {
                            const updated = deliveryStatuses.filter(s => s !== status);
                            dispatch(updateParameter({ deliveryStatuses: updated.length ? updated : [] }));
                        }}
                        data-test-selector="shipmentHistory_statusTag"
                    >
                        {`${translate("{0}:", translate("Status"))} ${getStatusDisplay(status)}`}
                    </Tag>
                ))}
            {fromDate && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => dispatch(updateParameter({ fromDate: "" }))}
                    data-test-selector="shipmentHistory_deliveryDateFromTag"
                >
                    {`${translate("{0}:", "Delivery Date From")} ${getLocalizedDateTime({
                        dateTime: convertDateOnlyStringToDate(fromDate)!,
                        language,
                    })}`}
                </Tag>
            )}
            {toDate && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => dispatch(updateParameter({ toDate: "" }))}
                    data-test-selector="shipmentHistory_deliveryDateToTag"
                >
                    {`${translate("{0}:", "Delivery Date To")} ${getLocalizedDateTime({
                        dateTime: convertDateOnlyStringToDate(toDate)!,
                        language,
                    })}`}
                </Tag>
            )}
            {isNextDaysFilter && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => dispatch(updateParameter({ isNextDaysFilter: undefined }))}
                >
                    {translate("Next 7 Days Shipments")}
                </Tag>
            )}
            {locationFilter && (
                <Tag {...styles.appliedFilterTag} onDelete={() => dispatch(updateParameter({ location: "" }))}>
                    {`${translate("{0}:", translate("Greif Location"))} ${locationDescription}`}
                </Tag>
            )}
        </TagWrapper>
    );
};

export default ShipmentHistoryFilterTags;
